"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PatVerbScope = exports.PAT_SCOPE_REGISTRY = void 0;
exports.getAllScopes = getAllScopes;
exports.getScopesByService = getScopesByService;
exports.getServiceLabel = getServiceLabel;
exports.getResourceLabel = getResourceLabel;
exports.expandWildcard = expandWildcard;
exports.isValidPatScope = isValidPatScope;
const zod_1 = require("zod");
exports.PAT_SCOPE_REGISTRY = {
    // ── File Storage (apps/fs) ──────────────────────────────────────
    fs: {
        label: "File Storage",
        resources: {
            files: {
                label: "Files",
                actions: ["read", "write", "delete"],
            },
            directories: {
                label: "Directories",
                actions: ["read", "write", "delete"],
            },
        },
    },
    // ── Knowledge Base (apps/kb) ────────────────────────────────────
    kb: {
        label: "Knowledge Base",
        resources: {
            docs: {
                label: "Documents",
                actions: ["read", "write", "delete"],
            },
        },
    },
    // ── LLM Load Balancer (apps/llm) ───────────────────────────────
    llm: {
        label: "Language Models",
        resources: {
            completions: {
                label: "Completions",
                actions: ["write"],
            },
            embeddings: {
                label: "Embeddings",
                actions: ["write"],
            },
            models: {
                label: "Models",
                actions: ["read", "manage"],
            },
            files: {
                label: "Files",
                actions: ["read", "write"],
            },
            tools: {
                label: "MCP Tools",
                actions: ["read", "execute"],
            },
            analytics: {
                label: "Analytics",
                actions: ["read"],
            },
            workers: {
                label: "Worker Management",
                actions: ["read", "manage"],
            },
        },
    },
    // ── Image Generation (apps/image-gen) ──────────────────────────
    "image-gen": {
        label: "Image Generation",
        resources: {
            batches: {
                label: "Generation Batches",
                actions: ["read", "write", "delete"],
            },
            models: {
                label: "Models",
                actions: ["read"],
            },
            prompts: {
                label: "Prompt Generation",
                actions: ["write"],
            },
            projects: {
                label: "Projects",
                actions: ["read", "write"],
            },
        },
    },
    // ── Code Sandbox (apps/sandbox) ────────────────────────────────
    sandbox: {
        label: "Code Execution",
        resources: {
            execution: {
                label: "Code Execution",
                actions: ["execute"],
            },
            sessions: {
                label: "Sessions",
                actions: ["read", "write", "delete"],
            },
        },
    },
    // ── Speech Services (apps/s2s) ─────────────────────────────────
    s2s: {
        label: "Speech Services",
        resources: {
            realtime: {
                label: "Realtime Voice",
                actions: ["connect"],
            },
            transcribe: {
                label: "Transcription",
                actions: ["execute"],
            },
        },
    },
    // ── Transcribe App (apps/transcribe) ───────────────────────────
    transcribe: {
        label: "Transcription App",
        resources: {
            summaries: {
                label: "Summaries",
                actions: ["write"],
            },
        },
    },
    // ── URL Shortener (apps/api/shortener) ─────────────────────────
    shortener: {
        label: "URL Shortener",
        resources: {
            links: {
                label: "Short Links",
                actions: ["read", "write", "delete"],
            },
            analytics: {
                label: "Analytics",
                actions: ["read"],
            },
        },
    },
    // ── Docs Platform (apps/docs) ──────────────────────────────────
    docs: {
        label: "Documents",
        resources: {
            documents: {
                label: "Documents",
                actions: ["read", "write", "delete"],
            },
            collections: {
                label: "Collections",
                actions: ["read", "write", "delete"],
            },
            versions: {
                label: "Versions",
                actions: ["read"],
            },
            templates: {
                label: "Templates",
                actions: ["read"],
            },
            ai: {
                label: "AI Assistant",
                actions: ["execute"],
            },
        },
    },
    // ── Canvas (apps/canvas) ───────────────────────────────────────
    canvas: {
        label: "Canvas",
        resources: {
            workspaces: {
                label: "Workspaces",
                actions: ["read", "write", "delete"],
            },
            versions: {
                label: "Versions",
                actions: ["read", "write", "delete"],
            },
            sharing: {
                label: "Sharing",
                actions: ["read", "write"],
            },
        },
    },
    // ── App Storage (apps/api/storage) ─────────────────────────────
    storage: {
        label: "App Storage",
        resources: {
            items: {
                label: "Storage Items",
                actions: ["read", "list", "write", "delete"],
            },
        },
    },
    // ── Dashboard (apps/api/dashboard) ─────────────────────────────
    dashboard: {
        label: "Dashboard",
        resources: {
            config: {
                label: "Configuration",
                actions: ["read", "write"],
            },
            cards: {
                label: "Cards",
                actions: ["read", "write", "delete"],
            },
            events: {
                label: "Live Events",
                actions: ["read"],
            },
        },
    },
    // ── Agent Platform (apps/agent-api) ────────────────────────────
    agent: {
        label: "Agent Platform",
        resources: {
            tasks: {
                label: "Tasks",
                actions: ["read", "write", "delete"],
            },
            files: {
                label: "Files",
                actions: ["write"],
            },
        },
    },
    // ── MCP Proxy (apps/mcp-proxy) ─────────────────────────────────
    mcp: {
        label: "MCP Hub",
        resources: {
            tools: {
                label: "Tools",
                actions: ["read", "execute"],
            },
            resources: {
                label: "Resources",
                actions: ["read"],
            },
            prompts: {
                label: "Prompts",
                actions: ["read"],
            },
            servers: {
                label: "User Servers",
                actions: ["read", "write", "delete"],
            },
            admin: {
                label: "Admin",
                actions: ["read", "write"],
            },
        },
    },
    // ── Benchmarks (apps/benchmarks) ───────────────────────────────
    benchmarks: {
        label: "Benchmarks",
        resources: {
            jobs: {
                label: "Benchmark Jobs",
                actions: ["read", "write", "delete"],
            },
            models: {
                label: "Model Catalog",
                actions: ["read", "write"],
            },
            results: {
                label: "Results",
                actions: ["read"],
            },
            speed: {
                label: "Speed Tests",
                actions: ["read", "execute"],
            },
        },
    },
    // ── CORS Proxy (apps/proxy) ────────────────────────────────────
    proxy: {
        label: "CORS Proxy",
        resources: {
            requests: {
                label: "Proxy Requests",
                actions: ["read", "write"],
            },
        },
    },
    // ── Infrastructure Viz (apps/api/infra-viz) ────────────────────
    infra: {
        label: "Infrastructure",
        resources: {
            topology: {
                label: "Service Topology",
                actions: ["read", "write"],
            },
            health: {
                label: "Health Checks",
                actions: ["execute"],
            },
            logs: {
                label: "Service Logs",
                actions: ["read"],
            },
            events: {
                label: "Infrastructure Events",
                actions: ["read"],
            },
            observability: {
                label: "Observability",
                actions: ["read", "write", "delete"],
            },
        },
    },
    // ── Auth Self-Service (apps/api/auth) ──────────────────────────
    auth: {
        label: "Account",
        resources: {
            profile: {
                label: "Profile",
                actions: ["read", "write"],
            },
            sessions: {
                label: "Sessions",
                actions: ["read", "delete"],
            },
            files: {
                label: "Files",
                actions: ["read", "write", "delete"],
            },
            pats: {
                label: "Access Tokens",
                actions: ["read", "write", "delete"],
            },
            identity: {
                label: "Identity",
                actions: ["read", "write", "delete"],
            },
            webauthn: {
                label: "Passkeys",
                actions: ["read", "write", "delete"],
            },
            oauth: {
                label: "Linked Accounts",
                actions: ["read", "delete"],
            },
            delegations: {
                label: "Delegations",
                actions: ["read"],
            },
        },
    },
    // ── Entity API (apps/api/api) ──────────────────────────────────
    entities: {
        label: "Entities",
        resources: {
            items: {
                label: "Entity Items",
                actions: ["read", "write", "delete"],
            },
        },
    },
    // ── Brain (apps/brain) — personal data plane (tdd-0021) ─────────
    // Sensitivity tiers (dl-0003) are modelled as resources so a token can be
    // granted read up to a tier: brain:memory:read (normal), brain:sensitive:read,
    // brain:secret:read. brain:memory:capture writes.
    brain: {
        label: "Brain (personal memory)",
        resources: {
            memory: {
                label: "Memory (normal)",
                actions: ["read", "capture"],
            },
            sensitive: {
                label: "Sensitive memory",
                actions: ["read"],
            },
            secret: {
                label: "Secret memory",
                actions: ["read"],
            },
        },
    },
};
// ── Derived helpers ─────────────────────────────────────────────────
/**
 * Get all valid leaf scopes (service:resource:action) from the registry.
 */
function getAllScopes() {
    const scopes = [];
    for (const [service, def] of Object.entries(exports.PAT_SCOPE_REGISTRY)) {
        for (const [resource, rDef] of Object.entries(def.resources)) {
            for (const action of rDef.actions) {
                scopes.push(`${service}:${resource}:${action}`);
            }
        }
    }
    return scopes;
}
/**
 * Get all valid leaf scopes for a single service.
 */
function getScopesByService(service) {
    const def = exports.PAT_SCOPE_REGISTRY[service];
    if (!def)
        return [];
    const scopes = [];
    for (const [resource, rDef] of Object.entries(def.resources)) {
        for (const action of rDef.actions) {
            scopes.push(`${service}:${resource}:${action}`);
        }
    }
    return scopes;
}
/**
 * Get the human-readable label for a service.
 */
function getServiceLabel(service) {
    return exports.PAT_SCOPE_REGISTRY[service]?.label;
}
/**
 * Get the human-readable label for a resource within a service.
 */
function getResourceLabel(service, resource) {
    const def = exports.PAT_SCOPE_REGISTRY[service];
    if (!def)
        return undefined;
    return def.resources[resource]?.label;
}
/**
 * Expand a wildcard scope to all leaf scopes it covers.
 *  - `*` → all scopes
 *  - `llm:*` → all llm:resource:action
 *  - `llm:completions:*` → all llm:completions:action
 *  - `llm:completions:write` → ['llm:completions:write'] (no-op)
 */
function expandWildcard(scope) {
    if (scope === "*")
        return getAllScopes();
    const parts = scope.split(":");
    if (parts.length === 2 && parts[1] === "*") {
        return getScopesByService(parts[0]);
    }
    if (parts.length === 3 && parts[2] === "*") {
        const [service, resource] = parts;
        const def = exports.PAT_SCOPE_REGISTRY[service];
        if (!def)
            return [];
        const rDef = def.resources[resource];
        if (!rDef)
            return [];
        return rDef.actions.map((a) => `${service}:${resource}:${a}`);
    }
    // Not a wildcard — return as-is if valid
    return isValidPatScope(scope) ? [scope] : [];
}
// ── Scope string format ─────────────────────────────────────────────
// Valid formats:
//   *                          (global wildcard)
//   service:*                  (service wildcard)
//   service:resource:*         (resource wildcard)
//   service:resource:action    (leaf scope)
// Wildcards may only appear as the final segment.
const SCOPE_PATTERN = /^(?:\*|[a-z][a-z0-9-]*:\*|[a-z][a-z0-9-]*:[a-z][a-z0-9-]*:(?:\*|[a-z][a-z0-9-]*))$/;
/**
 * Check if a scope string is structurally valid AND exists in the registry.
 */
function isValidPatScope(scope) {
    if (!SCOPE_PATTERN.test(scope))
        return false;
    // Global wildcard
    if (scope === "*")
        return true;
    const parts = scope.split(":");
    // service:*
    if (parts.length === 2 && parts[1] === "*") {
        return parts[0] in exports.PAT_SCOPE_REGISTRY;
    }
    // service:resource:* or service:resource:action
    if (parts.length === 3) {
        const [service, resource, action] = parts;
        const def = exports.PAT_SCOPE_REGISTRY[service];
        if (!def)
            return false;
        const rDef = def.resources[resource];
        if (!rDef)
            return false;
        if (action === "*")
            return true;
        return rDef.actions.includes(action);
    }
    return false;
}
// ── Zod schema ──────────────────────────────────────────────────────
/**
 * Zod schema that validates a single PAT verb scope string against the registry.
 */
exports.PatVerbScope = zod_1.z.string().refine(isValidPatScope, {
    message: "Invalid PAT scope. Must be a valid service:resource:action, service:resource:*, service:*, or *",
});
